/**
 * Pure mapping from a backend CampaignRow (GET /campaigns, /campaigns/:id,
 * /campaigns/summary.active_campaigns) to the row shape the CampaignOS tables and
 * drawer were built around. Keeping this in one place means every screen shows
 * the same name / status / progress / response figures for a campaign.
 *
 * Backend row (see vendorhub-backend/src/modules/campaigns/campaigns.service.ts):
 *   { id, type: 'REGULARIZATION'|'BROADCAST', subject, status: QUEUED|SENDING|SENT|FAILED,
 *     ui_status: scheduled|running|completed|failed, created_at, updated_at,
 *     creator: {id, full_name}|null, regularization_batch_id,
 *     counts: {total_count, sent_count, delivered_count, opened_count, clicked_count, bounced_count, failed_count},
 *     rates: {delivery, open, click, bounce},
 *     regularization: { batch_status, sent_at, completed_at,
 *                       requests: {sent, submitted, verified, expired, total}, response_rate } | null }
 */

const DAY_MS = 86_400_000;
/** Regularization links expire 7 days after the batch is sent (regularization.service.ts). */
export const REGULARIZATION_LINK_TTL_DAYS = 7;

export const CAMPAIGN_TYPE_LABEL = {
  REGULARIZATION: 'Regularization request',
  BROADCAST: 'Broadcast',
};

export const API_STATUS_LABEL = { QUEUED: 'Queued', SENDING: 'Sending', SENT: 'Sent', FAILED: 'Failed' };

export function campaignName(row) {
  if (!row) return '';
  // The wizard stores an operator-facing name; older/ad-hoc sends fall back to the
  // subject, and regularization batches keep their batch label.
  if (row.name) return row.name;
  if (row.type === 'REGULARIZATION') {
    return `Regularization — batch #${row.regularization_batch_id ?? row.id}`;
  }
  return row.subject || `Campaign #${row.id}`;
}

const isoDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : null);

export function mapCampaign(row) {
  if (!row) return null;
  const c = row.counts || {};
  const total = c.total_count || 0;
  const sent = c.sent_count || 0;
  const failed = c.failed_count || 0;
  const bounced = c.bounced_count || 0;
  const processed = sent + failed;
  const progress = total ? Math.round((processed / total) * 100) : 0;
  const reg = row.regularization || null;

  // "Response" is a REAL vendor response when we can measure one (a regularization
  // form submitted/verified); otherwise it is the email open rate.
  const responded = reg ? reg.requests.submitted + reg.requests.verified : c.opened_count || 0;
  const responseRate = reg ? reg.response_rate : row.rates ? row.rates.open : 0;
  const responseLabel = reg ? 'Form response' : 'Open rate';

  const deadline = reg?.sent_at
    ? isoDate(new Date(new Date(reg.sent_at).getTime() + REGULARIZATION_LINK_TTL_DAYS * DAY_MS))
    : null;

  return {
    // Identity
    id: `CMP-${row.id}`,
    rawId: row.id,
    name: campaignName(row),
    subject: row.subject,
    type: row.type,
    templateName: CAMPAIGN_TYPE_LABEL[row.type] || row.type,
    templateId: row.template_id ?? null,
    // Status
    status: row.ui_status,
    apiStatus: row.status,
    apiStatusLabel: API_STATUS_LABEL[row.status] || row.status,
    // Audience / progress
    audience: reg ? `Regularization batch #${row.regularization_batch_id}` : 'Vendor broadcast',
    audienceSize: total,
    progress,
    channel: 'Email',
    contacted: sent,
    responded,
    responseRate,
    responseLabel,
    failed: failed + bounced,
    // People / dates
    owner: row.creator?.full_name || '—',
    created: isoDate(row.created_at),
    updated: isoDate(row.updated_at),
    deadline,
    // Raw figures for the drawer
    counts: {
      total_count: total,
      sent_count: sent,
      delivered_count: c.delivered_count || 0,
      opened_count: c.opened_count || 0,
      clicked_count: c.clicked_count || 0,
      bounced_count: bounced,
      failed_count: failed,
    },
    rates: row.rates || { delivery: 0, open: 0, click: 0, bounce: 0 },
    regularization: reg,
    regularizationBatchId: row.regularization_batch_id ?? null,
  };
}

export function mapCampaigns(rows) {
  return (rows || []).map(mapCampaign).filter(Boolean);
}

export default mapCampaign;
